import React, { useState } from 'react';
import {
  Box, Tabs, Tab, Typography, Paper, Divider, Container,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  IconButton, Collapse, useMediaQuery, CardContent,
  Card,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Event as EventIcon,
  Assessment as ResultsIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import EventManagerFiles from './EventManagerFiles';
import EventsTable from '../../Eventos/EventsTable';
import EventHeats from '../../Eventos/EventsHeats/EventHeats';
import FileUploadAccordions from '../../Eventos/FileUploadAccordions';
import TableroSelector from '../../Tableros/TableroSelector';


export default function FinishLynxIntegration({ 
  fullScreen = false, 
  isBottomSheet = false,
  isMobileView = false
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) || isMobileView;
  const isXSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estado para panels expandibles (útiles para vista móvil)
  const [expandedPanel, setExpandedPanel] = useState(null);
  
  // Estado para controlar el modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvt, setSelectedEvt] = useState({ eventNumber: null, round: null });

  // Cuando la tabla llama a onEventSelect
  const handleEventSelect = (eventNumber, round) => {
    setSelectedEvt({ eventNumber, round });
    setModalOpen(true);
  };
  
  const handleClose = () => setModalOpen(false);
  
  const togglePanel = (panel) => {
    setExpandedPanel(expandedPanel === panel ? null : panel);
  };

  const [tab, setTab] = useState(0);
  const handleChange = (_, v) => setTab(v);

  // Si estamos en bottom sheet, mostrar contenido directo según la pestaña
  if (isBottomSheet) {
    return (
      <Box sx={{ height: '100%', overflow: 'auto' }}>
        {tab === 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Gestión de Archivos</Typography>
            <EventManagerFiles />
          </Box>
        )}
        
        {tab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Eventos Detectados</Typography>
            <EventsTable onEventSelect={handleEventSelect} />
          </Box>
        )}
        
        {tab === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Tableros</Typography>
            <TableroSelector />
          </Box>
        )}
        
        {/* Modal para bottom sheet */}
        <Dialog
          open={modalOpen}
          onClose={handleClose}
          maxWidth="lg"
          fullWidth
          fullScreen={isXSmall}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            p: isXSmall ? 1.5 : 2 
          }}>
            <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <EventHeats
              eventNumber={selectedEvt.eventNumber}
              round={selectedEvt.round}
              readOnly
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="contained" color="primary">Cerrar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Para dispositivos muy pequeños en pantalla normal, usar un diseño de tarjetas expandibles
  if (isXSmall && !fullScreen && !isBottomSheet) {
    return (
      <Container 
        disableGutters={fullScreen} 
        sx={{ 
          p: fullScreen ? 0 : 1,
          height: '100%'
        }}
      >
        
        <Box sx={{ mb: 2 }}>
          <Card 
            sx={{ 
              mb: 1.5,
              borderLeft: expandedPanel === 0 ? `4px solid ${theme.palette.primary.main}` : 'none'
            }}
          >
            <CardContent
              sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1.5,
                '&:last-child': { pb: 1.5 }
              }}
              onClick={() => togglePanel(0)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <UploadIcon color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="subtitle1">Subir Archivos</Typography>
              </Box>
              <IconButton size="small">
                {expandedPanel === 0 ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </CardContent>
            <Collapse in={expandedPanel === 0}>
              <Box sx={{ px: 2, pb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <EventManagerFiles />
              </Box>
            </Collapse>
          </Card>
          
          <Card 
            sx={{ 
              mb: 1.5,
              borderLeft: expandedPanel === 1 ? `4px solid ${theme.palette.primary.main}` : 'none'
            }}
          >
            <CardContent
              sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1.5,
                '&:last-child': { pb: 1.5 }
              }}
              onClick={() => togglePanel(1)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EventIcon color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="subtitle1">Eventos Procesados</Typography>
              </Box>
              <IconButton size="small">
                {expandedPanel === 1 ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </CardContent>
            <Collapse in={expandedPanel === 1}>
              <Box sx={{ px: 2, pb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <EventsTable onEventSelect={handleEventSelect} />
              </Box>
            </Collapse>
          </Card>
          
          <Card 
            sx={{ 
              mb: 1.5,
              borderLeft: expandedPanel === 2 ? `4px solid ${theme.palette.primary.main}` : 'none'
            }}
          >
            <CardContent
              sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1.5,
                '&:last-child': { pb: 1.5 }
              }}
              onClick={() => togglePanel(2)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ResultsIcon color="primary" sx={{ mr: 1.5 }} />
                <Typography variant="subtitle1">Resultados</Typography>
              </Box>
              <IconButton size="small">
                {expandedPanel === 2 ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </CardContent>
            <Collapse in={expandedPanel === 2}>
              <Box sx={{ px: 2, pb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <TableroSelector />
              </Box>
            </Collapse>
          </Card>
        </Box>
        
        {/* Modal para pantallas pequeñas */}
        <Dialog
          open={modalOpen}
          onClose={handleClose}
          maxWidth="lg"
          fullWidth
          fullScreen={isXSmall}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5
          }}>
            <Typography variant="subtitle1">
              Heats E{selectedEvt.eventNumber}-R{selectedEvt.round}
            </Typography>
            <IconButton edge="end" size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1 }}>
            <EventHeats
              eventNumber={selectedEvt.eventNumber}
              round={selectedEvt.round}
              readOnly
            />
          </DialogContent>
          <DialogActions sx={{ p: 1 }}>
            <Button onClick={handleClose} size="small" variant="contained">Cerrar</Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // Versión normal para pantallas más grandes
  return (
<Container 
  maxWidth={fullScreen ? false : false}
  disableGutters={true}
  sx={{ 
    mt: fullScreen ? 0 : 2, 
    mb: fullScreen ? 0 : 2,
    // Don't restrict height to 100%
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  }}
>
  <Paper
    elevation={fullScreen ? 0 : 3}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      // Remove height restrictions
      overflow: 'visible',
      borderRadius: fullScreen ? 0 : 2,
      border: fullScreen ? 'none' : `1px solid ${theme.palette.divider}`,
      flexGrow: 1
    }}
  >
        <Tabs
          orientation="horizontal" // Siempre horizontal
          value={tab}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            ...(!isMobile && { borderRight: 1, borderColor: 'divider', minWidth: isXSmall ? 60 : 180 }),
            ...(isMobile && { borderBottom: 1, borderColor: 'divider', width: '100%' })
          }}
        >
          <Tab 
            icon={<UploadIcon />} 
            iconPosition={isMobile || isXSmall ? "top" : "start"}
            label={isXSmall ? undefined : (isMobile ? 'Subir' : 'Subir Archivos')} 
          />
          <Tab 
            icon={<EventIcon />} 
            iconPosition={isMobile || isXSmall ? "top" : "start"}
            label={isXSmall ? undefined : (isMobile ? 'Eventos' : 'Eventos Procesados')} 
          />
          <Tab 
            icon={<ResultsIcon />} 
            iconPosition={isMobile || isXSmall ? "top" : "start"}
            label={isXSmall ? undefined : (isMobile ? 'Tableros' : 'Tableros')} 
          />
        </Tabs>

        {/* ── Sub-paneles ── */}
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'visible',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {tab === 0 && (
            <Box sx={{ 
              p: isXSmall ? 1 : (isMobile ? 2 : 3),
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible'
            }}>
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="h6">Gestión de Archivos</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ flexGrow: 1, overflow: 'visible' }}>
                <FileUploadAccordions />
              </Box>
            </Box>
          )}
          
          {tab === 1 && (
            <Box sx={{ 
              p: isXSmall ? 1 : (isMobile ? 2 : 3),
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible'
            }}>
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="h6">Eventos Detectados</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <EventsTable 
                  onEventSelect={handleEventSelect} 
                  fullScreen={fullScreen}
                />
              </Box>
            </Box>
          )}
          
          {tab === 2 && (
            <Box sx={{ 
              p: isXSmall ? 1 : (isMobile ? 2 : 3),
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible'
            }}>
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="h6">Tableros</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ flexGrow: 1, overflow: 'visible' }}>
                <TableroSelector />
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* ── Modal de Heats ── */}
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isXSmall}
        sx={{
          '& .MuiDialog-paper': {
            height: isXSmall ? '100%' : '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          p: isXSmall ? 1.5 : 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          dividers
          sx={{ 
            p: isXSmall ? 1 : 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible'
          }}
        >
          <Box sx={{ flexGrow: 1, overflow: 'visible' }}>
            {/* Le pasamos por props eventNumber y round */}
            <EventHeats
              eventNumber={selectedEvt.eventNumber}
              round={selectedEvt.round}
              // si quieres que sea sólo lectura:
              readOnly
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: isXSmall ? 1 : 1.5 }}>
          <Button 
            onClick={handleClose} 
            variant="contained" 
            sx={{ minWidth: 100 }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}